import { useState, type CSSProperties } from "react";
import { Line, LineChart, ResponsiveContainer } from "recharts";
import type { MatchEvent } from "../../../../store/useMatchLogStore";
import TeamLogo, { type TeamLogoProps } from "../../../../components/TeamLogo";
import { resolveMomentumColors, momentumStrokeFor } from "./momentumColors";
import { useBallRecoveryTime } from "./useMomentumAnalytics";
import type { TeamRecovery } from "./momentumTypes";
import {
  resolveChartColor,
  DEFAULT_HOME_COLOR,
  DEFAULT_AWAY_COLOR,
} from "../chartColor";

// Team crests grow from "md" (40px) to "lg" (48px) at the `sm` breakpoint —
// a single DOM node via a `!`-important className override, since
// `TeamLogo`'s size prop sets an inline `style` that a plain Tailwind class
// cannot outrank (PMA-COCK-161, matches MatchMomentumChart's convention).
const CREST_RESPONSIVE_CLASS = "sm:!w-12 sm:!h-12 sm:!min-w-12";

export interface BallRecoveryTimeBannerProps {
  events: MatchEvent[];
  homeTeamId: string;
  awayTeamId: string;
  homeLabel: string;
  awayLabel: string;
  homeTeamColor?: string;
  awayTeamColor?: string;
  /**
   * Secondary team colors (PMA-COCK-180) — the chromatic fallback
   * `resolveChartColor` uses when `homeTeamColor`/`awayTeamColor` is
   * achromatic (black/near-black) and brightening it wouldn't produce a
   * recognizable team color. Optional; omitting either behaves exactly as
   * before this UC.
   */
  homeTeamSecondaryColor?: string;
  awayTeamSecondaryColor?: string;
  /**
   * Team objects (PMA-COCK-161) used to render a crest beside each team's
   * time — home immediately left, away immediately right. Optional;
   * omitting either/both reproduces today's exact layout.
   */
  homeTeam?: TeamLogoProps["team"];
  awayTeam?: TeamLogoProps["team"];
  t: (key: string, fallback?: string) => string;
}

function Sparkline({
  data,
  color,
}: {
  data: TeamRecovery["byPeriod"];
  color: string;
}) {
  if (data.length <= 1) return null;
  return (
    <div className="w-16 h-5 mt-0.5" aria-hidden="true">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          accessibilityLayer={false}
          aria-hidden="true"
          {...{ focusable: "false" }}
        >
          <Line
            type="monotone"
            dataKey="avgSeconds"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function TimeDisplay({
  label,
  textColor,
  fillColor,
  strokeColor,
  team,
  testId,
  isFastest,
  align,
  fastestLabel,
  crestTeam,
}: {
  label: string;
  /** Seconds number + "Fastest" chip text (PMA-COCK-180, role "text"). */
  textColor: string;
  /** "Fastest" chip background, alpha-22 (PMA-COCK-180, role "fill"). */
  fillColor: string;
  /** Sparkline line (PMA-COCK-180, role "stroke"). */
  strokeColor: string;
  team: TeamRecovery;
  testId: string;
  isFastest: boolean;
  align: "left" | "right";
  fastestLabel: string;
  crestTeam?: TeamLogoProps["team"];
}) {
  return (
    <div
      className={`flex flex-col ${
        align === "right" ? "items-end" : "items-start"
      }`}
      data-testid={testId}
    >
      <span className="text-[10px] text-slate-400 uppercase tracking-widest font-medium">
        {label}
      </span>
      <div className="flex items-center gap-1.5 sm:gap-2">
        {align === "left" && crestTeam && (
          <TeamLogo
            team={crestTeam}
            bare
            size="md"
            className={CREST_RESPONSIVE_CLASS}
            testId={`${testId}-crest`}
          />
        )}
        <span
          className="font-condensed text-5xl font-bold leading-none mt-0.5"
          style={{ color: textColor }}
        >
          {team.avgSeconds === null ? "—" : `${team.avgSeconds.toFixed(0)}s`}
        </span>
        {align === "right" && crestTeam && (
          <TeamLogo
            team={crestTeam}
            bare
            size="md"
            className={CREST_RESPONSIVE_CLASS}
            testId={`${testId}-crest`}
          />
        )}
      </div>
      {isFastest && team.avgSeconds !== null && (
        <span
          className="text-[9px] font-semibold uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded-full"
          style={{ background: `${fillColor}22`, color: textColor }}
        >
          ⚡ {fastestLabel}
        </span>
      )}
      <Sparkline data={team.byPeriod} color={strokeColor} />
    </div>
  );
}

function segmentStyle(width: number, color: string): CSSProperties {
  const outline = momentumStrokeFor(color);
  return {
    width: `${width}%`,
    background: color,
    ...(outline ? { boxShadow: `inset 0 0 0 1px ${outline}` } : {}),
  };
}

function TugOfWarBar({
  home,
  away,
  homeColor,
  awayColor,
}: {
  home: TeamRecovery;
  away: TeamRecovery;
  homeColor: string;
  awayColor: string;
}) {
  if (home.avgSeconds === null || away.avgSeconds === null) return null;
  const total = home.avgSeconds + away.avgSeconds;
  // Faster team (lower seconds) gets the larger share of the bar.
  const homeSharePct = (away.avgSeconds / total) * 100;
  const awaySharePct = 100 - homeSharePct;
  return (
    <div className="mt-3 px-1">
      <div className="flex rounded-full overflow-hidden h-1.5">
        <div
          data-testid="recovery-bar-home"
          className="motion-safe:transition-all motion-safe:duration-700"
          style={segmentStyle(homeSharePct, homeColor)}
        />
        <div
          data-testid="recovery-bar-away"
          className="motion-safe:transition-all motion-safe:duration-700"
          style={segmentStyle(awaySharePct, awayColor)}
        />
      </div>
    </div>
  );
}

export function BallRecoveryTimeBanner({
  events,
  homeTeamId,
  awayTeamId,
  homeLabel,
  awayLabel,
  homeTeamColor = DEFAULT_HOME_COLOR,
  awayTeamColor = DEFAULT_AWAY_COLOR,
  homeTeamSecondaryColor,
  awayTeamSecondaryColor,
  homeTeam,
  awayTeam,
  t,
}: BallRecoveryTimeBannerProps) {
  const [explainOpen, setExplainOpen] = useState(false);

  const { home, away } = useBallRecoveryTime(events, {
    homeTeamId,
    awayTeamId,
  });
  const hasData = home.sampleCount > 0 || away.sampleCount > 0;

  const fastestLabel = t("analytics.recoveryFastest", "Fastest");

  const bothKnown = home.avgSeconds !== null && away.avgSeconds !== null;
  const homeFastest = bothKnown && home.avgSeconds! < away.avgSeconds!;
  const awayFastest = bothKnown && away.avgSeconds! < home.avgSeconds!;

  // Display-safe variants (PMA-COCK-180): each surface (text / fill / hairline
  // stroke) has its own legibility floor against the near-black panel bg.
  // Colors that already clear their floor pass through unchanged — this file
  // had no such floor before this UC, which is exactly how a black/near-black
  // team color went missing here.
  const homeTextColor = resolveChartColor(
    homeTeamColor,
    homeTeamSecondaryColor,
    "text",
  );
  const awayTextColor = resolveChartColor(
    awayTeamColor,
    awayTeamSecondaryColor,
    "text",
  );
  const homeFillColor = resolveChartColor(
    homeTeamColor,
    homeTeamSecondaryColor,
    "fill",
  );
  const awayFillColor = resolveChartColor(
    awayTeamColor,
    awayTeamSecondaryColor,
    "fill",
  );
  const homeStrokeColor = resolveChartColor(
    homeTeamColor,
    homeTeamSecondaryColor,
    "stroke",
  );
  const awayStrokeColor = resolveChartColor(
    awayTeamColor,
    awayTeamSecondaryColor,
    "stroke",
  );

  const barColors = resolveMomentumColors(
    { primary: homeTeamColor, secondary: homeTeamSecondaryColor },
    { primary: awayTeamColor, secondary: awayTeamSecondaryColor },
  );

  return (
    <div className="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 rounded-2xl p-4 sm:p-5 md:p-6 text-white shadow-xl">
      {!hasData ? (
        <div className="text-center">
          <h3 className="text-lg font-semibold font-condensed uppercase tracking-wide mb-1">
            {t("analytics.recoveryTitle", "Ball Recovery Time")}
          </h3>
          <p
            className="text-sm text-slate-400 py-4"
            data-testid="recovery-empty"
          >
            {t("analytics.recoveryEmpty", "Not enough recoveries logged yet")}
          </p>
        </div>
      ) : (
        <>
          {/* Broadcast band: home time | center label | away time */}
          <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
            <TimeDisplay
              label={homeLabel}
              textColor={homeTextColor}
              fillColor={homeFillColor}
              strokeColor={homeStrokeColor}
              team={home}
              testId="recovery-home"
              isFastest={homeFastest}
              align="left"
              fastestLabel={fastestLabel}
              crestTeam={homeTeam}
            />
            <div className="text-center px-2 min-w-0">
              <h3 className="text-sm font-semibold font-condensed uppercase tracking-widest text-slate-200 leading-tight">
                {t("analytics.recoveryTitle", "Ball Recovery Time")}
              </h3>
              <p className="text-[10px] text-slate-400 mt-0.5 leading-tight max-w-[110px]">
                {t(
                  "analytics.recoverySubtitle",
                  "Avg. time to win the ball back",
                )}
              </p>
            </div>
            <TimeDisplay
              label={awayLabel}
              textColor={awayTextColor}
              fillColor={awayFillColor}
              strokeColor={awayStrokeColor}
              team={away}
              testId="recovery-away"
              isFastest={awayFastest}
              align="right"
              fastestLabel={fastestLabel}
              crestTeam={awayTeam}
            />
          </div>
          <TugOfWarBar
            home={home}
            away={away}
            // EPIC-111 D3: true kit colours with a light outline when too
            // dark for the card (same resolver as the momentum chart).
            homeColor={barColors.home}
            awayColor={barColors.away}
          />
        </>
      )}

      {/* Calculation explanation toggle — always visible on screen, hidden
          from the PDF/JPG export capture (PMA-COCK-170, client wave
          2026-09-07: "cortaría ahí para que no se vea en el pdf lo de cómo
          se calcula"). `data-export-hidden` is read by
          `captureSelectorAsImage`'s html2canvas `onclone` in
          MatchAnalytics.tsx, never by anything on-screen. */}
      <div
        className="mt-3 border-t border-slate-700/50 pt-2"
        data-export-hidden="true"
      >
        <button
          type="button"
          className="flex min-h-11 items-center gap-1 py-3 text-xs text-slate-400 hover:text-slate-300 transition-colors"
          aria-expanded={explainOpen}
          data-testid="recovery-explain-toggle"
          onClick={() => setExplainOpen((o) => !o)}
        >
          <span aria-hidden="true">ⓘ</span>
          {t("analytics.recoveryExplainToggle", "How is this calculated?")}
          <span aria-hidden="true" className="ml-0.5">
            {explainOpen ? "▲" : "▼"}
          </span>
        </button>
        {explainOpen && (
          <p
            className="mt-2 text-xs text-slate-400 leading-relaxed"
            data-testid="recovery-explain"
          >
            {t(
              "analytics.recoveryExplainBody",
              "The average time a team needs to win the ball back after losing it. We measure from the moment a team loses possession until it regains the ball (through an interception, tackle, or loose ball) and average those times. Lower is better: it shows how quickly a team presses to recover the ball.",
            )}
          </p>
        )}
      </div>
    </div>
  );
}
